import { Component, Vue } from 'nuxt-property-decorator';
import SearchHistoryQuery from '@/components/common/UniversalSearch/apollo/SearchHistory.query';
import CreateSearchQueryMutation from '@/components/common/UniversalSearch/apollo/CreateSearchQuery.mutation';
import DestroySearchQueryMutation from '@/components/common/UniversalSearch/apollo/DestroySearchQuery.mutation';
import { buildSearchItemData } from '@/components/common/UniversalSearch/utils';
import type { UniversalSearchResult } from '@/components/common/UniversalSearch/types';
import type { DataProxy } from '@apollo/client/core';
import type { SearchQueryConnection, SearchQuery } from '@/types/graphql';

const HISTORY_LIMIT = 5;

@Component({
  apollo: {
    searchHistory: {
      query: SearchHistoryQuery,
      variables() {
        return {
          first: HISTORY_LIMIT,
        };
      },
      error(e) {
        this.$notice.error(e);
      },
      update(data: { searchHistory: SearchQueryConnection }) {
        this.history = data.searchHistory.nodes;
      },
    },
  },
})
export default class UniversalSearchHistoryMixin extends Vue {
  protected history: SearchQuery[] = [];

  async createHistoryItem(query: string, item: UniversalSearchResult) {
    const searchItem = buildSearchItemData(item);
    const input = {
      query,
      referenceableId: searchItem?.id,
      searchResultCategory: item.category.toUpperCase(),
    };

    await this.$apollo.mutate({
      mutation: CreateSearchQueryMutation,
      variables: { input },
      update(store: DataProxy, result) {
        const data = store.readQuery<{ searchHistory: SearchQueryConnection }>({
          query: SearchHistoryQuery,
          variables: { first: HISTORY_LIMIT },
        });

        if (!result.data || !data) return;

        const newNode = result.data.createSearchQuery;
        // filter out the same nodes in case we already have this item in history
        const oldNodes = data.searchHistory.nodes.filter(
          node => node.searchResult && buildSearchItemData(node.searchResult)?.link !== searchItem?.link,
        );
        const newData = {
          ...data,
          searchHistory: {
            ...data.searchHistory,
            nodes: [newNode, ...oldNodes],
          },
        };

        store.writeQuery({ query: SearchHistoryQuery, variables: { first: HISTORY_LIMIT }, data: newData });
      },
    });
  }

  async destroyHistoryItem(id: string) {
    await this.$apollo.mutate({
      mutation: DestroySearchQueryMutation,
      variables: { input: { id } },
      update(store: DataProxy) {
        const data = store.readQuery<{ searchHistory: SearchQueryConnection }>({
          query: SearchHistoryQuery,
          variables: { first: HISTORY_LIMIT },
        });

        if (!data?.searchHistory) return;

        const newData = {
          ...data,
          searchHistory: {
            ...data.searchHistory,
            nodes: data.searchHistory.nodes.filter(node => node.id !== id),
          },
        };

        store.writeQuery({ query: SearchHistoryQuery, variables: { first: HISTORY_LIMIT }, data: newData });
      },
    });
  }
}
